import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  adminOrdersApi,
  type AddOrderItemRequest,
  type AddOrderNoteRequest,
  type ApprovePartialRequest,
  type AssignSalespersonRequest,
  type BulkUpdateOrderItemsRequest,
  type CancelOrderRequest,
  type CapturePaymentRequest,
  type ConfirmManualPaymentRequest,
  PAYMENT_PROOF_FILE_CATEGORY,
  type CreateAdditionalInvoiceRequest,
  type EtaRequest,
  type FlagOrderRequest,
  type ItemsAvailabilityRequest,
  type Order,
  type OrderParams,
  type OrderEmailInfo,
  type OrderStatusLog,
  type RefundOrderRequest,
  type ReturnRequestBody,
  type ReturnRequestDetail,
  type SendDocumentRequest,
  type SetTaxExemptionRequest,
  type UpdateOrderRequest,
  type UpdateOrderItemRequest,
  type UpdateOrderStatusRequest,
  type UpdateShippingAddressRequest,
  type VoidOrderRequest,
  type FraudReviewActionDto,
  type OrderCommissionData,
  type UpdateOrderCommissionRequest,
} from '@/apis/orders';
import type { PagedListResponse } from '@/apis/common.dto';
import { QUERY_KEYS } from '@/constants';

export const useAdminOrdersQuery = (params?: OrderParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', params],
    queryFn: () => adminOrdersApi.getOrders(params),
  });
};

export const useAdminOrderDetails = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
    queryFn: () => adminOrdersApi.getOrderById(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateOrderStatus = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateOrderStatusRequest) =>
      adminOrdersApi.updateOrderStatus(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Order status updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to update order status'
      );
    },
  });
};

export const useUpdateOrder = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateOrderRequest) =>
      adminOrdersApi.updateOrder(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Order updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update order');
    },
  });
};

export const useUpdateShippingAddress = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateShippingAddressRequest) =>
      adminOrdersApi.updateShippingAddress(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Shipping address updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update shipping address');
    },
  });
};

export const useAssignSalesperson = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AssignSalespersonRequest) =>
      adminOrdersApi.assignSalesperson(orderId, data),
    onSuccess: (updated) => {
      // Update detail cache
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      // Patch only salesperson fields in list caches — replacing the whole order would
      // inject detail-only relations (e.g. transactions) that the list endpoint doesn't return,
      // causing side-effects like the Stripe icon appearing unexpectedly.
      queryClient.setQueriesData<PagedListResponse<Order>>(
        { queryKey: [QUERY_KEYS.ORDERS, 'admin'], exact: false },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((o) =>
              o.id === orderId
                ? { ...o, assignedSalespersonId: updated.assignedSalespersonId, assignedSalesperson: updated.assignedSalesperson }
                : o
            ),
          };
        }
      );
      toast.success('Salesperson assigned successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to assign salesperson'
      );
    },
  });
};

export const useReportItemsAvailability = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ItemsAvailabilityRequest) =>
      adminOrdersApi.reportItemsAvailability(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Items availability reported successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to report items availability'
      );
    },
  });
};

export const useCancelOrder = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CancelOrderRequest) =>
      adminOrdersApi.cancelOrder(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Order cancelled successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    },
  });
};

export const useRefundOrder = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RefundOrderRequest) =>
      adminOrdersApi.refundOrder(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Refund processed successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    },
  });
};

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const useDownloadInvoice = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.downloadInvoice(orderId),
    onSuccess: (blob) => {
      triggerDownload(blob, `invoice-${orderId}.pdf`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to download invoice'
      );
    },
  });
};

export const useDownloadInvoiceNoShipping = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.downloadInvoiceNoShipping(orderId),
    onSuccess: (blob) => {
      triggerDownload(blob, `invoice-no-shipping-${orderId}.pdf`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to download invoice'
      );
    },
  });
};

export const useDownloadCCAuthForm = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.downloadCCAuthForm(orderId),
    onSuccess: (blob) => {
      triggerDownload(blob, `cc-auth-form-${orderId}.pdf`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to download CC auth form'
      );
    },
  });
};

export const useDownloadStatusConfirmation = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.downloadStatusConfirmation(orderId),
    onSuccess: (blob) => {
      triggerDownload(blob, `status-confirmation-${orderId}.pdf`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message ||
        'Failed to download status confirmation'
      );
    },
  });
};

export const useSendDocumentToCustomer = (orderId: string) => {
  return useMutation({
    mutationFn: (data: SendDocumentRequest) =>
      adminOrdersApi.sendDocument(orderId, data),
    onSuccess: () => {
      toast.success('Document request emailed to customer');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to send document request'
      );
    },
  });
};

export const useApprovePartial = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ApprovePartialRequest) =>
      adminOrdersApi.approvePartial(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Partial order approval updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to update partial approval'
      );
    },
  });
};

export const useCustomerOrders = (customerId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', 'customer', customerId],
    queryFn: () => adminOrdersApi.getOrdersByCustomer(customerId),
    enabled: !!customerId,
  });
};

// ─── Order relations ──────────────────────────────────────────────────────────

export const useChildOrders = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'children'],
    queryFn: () => adminOrdersApi.getChildOrders(orderId),
    enabled: !!orderId && enabled,
  });
};

export const useGroupOrder = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'group'],
    queryFn: () => adminOrdersApi.getGroupOrder(orderId),
    enabled: !!orderId,
  });
};

export const useParentOrder = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'parent'],
    queryFn: () => adminOrdersApi.getParentOrder(orderId),
    enabled: !!orderId,
  });
};

export const useDoubledOrder = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'doubled'],
    queryFn: () => adminOrdersApi.getDoubledOrder(orderId),
    enabled: !!orderId,
  });
};

export const useDownloadOrderReport = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.getOrderReport(orderId),
    onSuccess: (blob) => {
      triggerDownload(blob, `order-report-${orderId}.pdf`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to download order report'
      );
    },
  });
};

export const useUpdateOrderFlag = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FlagOrderRequest) =>
      adminOrdersApi.updateOrderFlag(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Order flag updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to update order flag'
      );
    },
  });
};

export const useVoidOrder = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VoidOrderRequest) =>
      adminOrdersApi.voidOrder(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Order voided');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to void order');
    },
  });
};

export const useApproveOrderEta = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EtaRequest) =>
      adminOrdersApi.approveOrderEta(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
      });
      toast.success('ETA updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update ETA');
    },
  });
};

export const useAddOrderItem = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddOrderItemRequest) =>
      adminOrdersApi.addOrderItem(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      // The Warehouse order page reads items through its own query.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Item added to order');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to add item');
    },
  });
};

export const useUpdateOrderItem = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      ...data
    }: UpdateOrderItemRequest & { itemId: string }) =>
      adminOrdersApi.updateOrderItem(orderId, itemId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      // The Warehouse order page reads items through its own query.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Item updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update item');
    },
  });
};

export const useDeleteOrderItem = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      adminOrdersApi.deleteOrderItem(orderId, itemId),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      // The Warehouse order page reads items through its own query.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Item removed');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to remove item');
    },
  });
};

export const useBulkUpdateOrderItems = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkUpdateOrderItemsRequest) =>
      adminOrdersApi.bulkUpdateOrderItems(orderId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success('Order updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update order');
    },
  });
};

export const useSetTaxExemption = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SetTaxExemptionRequest) =>
      adminOrdersApi.setTaxExemption(orderId, data),
    onSuccess: (updated, data) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WAREHOUSE, 'order', orderId] });
      toast.success(data.isTaxExempt ? 'Sales tax removed' : 'Sales tax restored');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update sales tax');
    },
  });
};

// ─── Order reviews ────────────────────────────────────────────────────────────

export const useAddOrderReview = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (review: string) => adminOrdersApi.addOrderReview(orderId, review),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Review added');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to add review');
    },
  });
};

export const useEditOrderReview = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, review }: { reviewId: string; review: string }) =>
      adminOrdersApi.editOrderReview(orderId, reviewId, review),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Review updated');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update review');
    },
  });
};

export const useDeleteOrderReview = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewId: string) => adminOrdersApi.deleteOrderReview(orderId, reviewId),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      toast.success('Review deleted');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to delete review');
    },
  });
};

// ─── Order files ──────────────────────────────────────────────────────────────

export const useOrderFiles = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'files'],
    queryFn: () => adminOrdersApi.getOrderFiles(orderId),
    enabled: !!orderId && enabled,
  });
};

export const useUploadOrderFile = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, category }: { file: File; category?: string }) =>
      adminOrdersApi.uploadOrderFile(orderId, file, category),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'files'],
      });
      toast.success('File uploaded');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to upload file');
    },
  });
};

export const useDeleteOrderFile = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      adminOrdersApi.deleteOrderFile(orderId, fileId),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
        exact: true,
      });
      toast.success('File deleted');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to delete file');
    },
  });
};

export const useUploadSalespersonAttachment = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      adminOrdersApi.uploadSalespersonAttachment(orderId, file),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
        exact: true,
      });
      toast.success('Attachment uploaded');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to upload attachment'
      );
    },
  });
};

// ─── Notes ────────────────────────────────────────────────────────────────────

export const useOrderNotes = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'notes'],
    queryFn: () => adminOrdersApi.getOrderNotes(orderId),
    enabled: !!orderId,
  });
};

export const useAddOrderNote = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddOrderNoteRequest) =>
      adminOrdersApi.addOrderNote(orderId, data),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
        exact: true,
      });
      toast.success('Note added');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to add note');
    },
  });
};

export const useSendFeedbackRequest = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.sendFeedbackRequest(orderId),
    onSuccess: () => {
      toast.success('Feedback request sent');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to send feedback request'
      );
    },
  });
};

export const useSendPartNotAvailable = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.sendPartNotAvailable(orderId),
    onSuccess: () => {
      toast.success('Part not available notification sent');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to send part not available notification'
      );
    },
  });
};

export const useSendPaymentRequest = (orderId: string) => {
  return useMutation({
    mutationFn: () => adminOrdersApi.sendPaymentRequest(orderId),
    onSuccess: () => {
      toast.success('Payment request sent');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to send payment request'
      );
    },
  });
};

// ─── Status logs ──────────────────────────────────────────────────────────────

export const useStatusLogs = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'status-logs'],
    queryFn: () => adminOrdersApi.getStatusLogs(orderId),
    enabled: !!orderId,
  });
};

export const useStatusLogById = (orderId: string, logId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'status-logs', logId],
    queryFn: () => adminOrdersApi.getStatusLogById(orderId, logId),
    enabled: !!orderId && !!logId,
  });
};

// ─── Payments ─────────────────────────────────────────────────────────────────

export const useOrderPayments = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'payments'],
    queryFn: () => adminOrdersApi.getOrderPayments(orderId),
    enabled: !!orderId,
  });
};

export const useCapturePayment = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CapturePaymentRequest) =>
      adminOrdersApi.capturePayment(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'payments'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
      });
      toast.success('Payment captured');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to capture payment');
    },
  });
};

export type ConfirmManualPaymentInput = Omit<ConfirmManualPaymentRequest, 'proofFileId'> & {
  proofFile?: File | null;
};

export const useConfirmManualPayment = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ proofFile, ...data }: ConfirmManualPaymentInput) => {
      let proofFileId: string | undefined;
      if (proofFile) {
        const withFile = await adminOrdersApi.uploadPaymentProof(orderId, proofFile);
        proofFileId = withFile.metadata?.files
          ?.filter((f) => f.category === PAYMENT_PROOF_FILE_CATEGORY)
          .at(-1)?.id;
      }
      return adminOrdersApi.confirmManualPayment(orderId, { ...data, proofFileId });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.ORDERS, 'admin', orderId], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
      toast.success('Order marked as paid');
    },
    onError: (error: { response?: { data?: { message?: string | string[] } } }) => {
      const message = error.response?.data?.message;
      toast.error((Array.isArray(message) ? message[0] : message) || 'Failed to mark order as paid');
    },
  });
};

export const usePaymentActions = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'payment-actions'],
    queryFn: () => adminOrdersApi.getPaymentActions(orderId),
    enabled: !!orderId,
  });
};

export const useCaptureDeadline = (orderId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'capture-deadline'],
    queryFn: () => adminOrdersApi.getCaptureDeadline(orderId),
    enabled: !!orderId && enabled,
  });
};

// ─── Returns ──────────────────────────────────────────────────────────────────

export const useOrderReturns = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'returns'],
    queryFn: () => adminOrdersApi.getOrderReturns(orderId),
    enabled: !!orderId,
  });
};

export const useSubmitReturnRequest = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReturnRequestBody) =>
      adminOrdersApi.submitReturnRequest(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'returns'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
      });
      toast.success('Return request submitted');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to submit return request'
      );
    },
  });
};

export const useReturnRequest = (orderId: string) => {
  return useQuery<ReturnRequestDetail | null>({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'return-request'],
    queryFn: () => adminOrdersApi.getReturnRequest(orderId),
    enabled: !!orderId,
  });
};

// ─── Order vendors ────────────────────────────────────────────────────────────

export const useOrderVendors = (orderId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'vendors'],
    queryFn: () => adminOrdersApi.getOrderVendors(orderId),
    enabled: !!orderId,
  });
};

// ─── Additional Invoice ────────────────────────────────────────────────────────

export const useCreateAdditionalInvoice = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAdditionalInvoiceRequest) =>
      adminOrdersApi.createAdditionalInvoice(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId],
      });
      toast.success('Additional invoice created');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to create additional invoice'
      );
    },
  });
};
// ─── Fraud Review ─────────────────────────────────────────────────────────────

export const useProcessFraudAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FraudReviewActionDto) =>
      adminOrdersApi.processFraudAction(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        [QUERY_KEYS.ORDERS, 'admin', updated.id],
        updated
      );
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error.response?.data?.message || 'Failed to process fraud action'
      );
    },
  });
};


export const useOrderStatusLogs = (orderId: string) => {
  return useQuery<OrderStatusLog[]>({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'status-logs'],
    queryFn: () => adminOrdersApi.getOrderStatusLogs(orderId),
    enabled: !!orderId,
  });
};

export const useOrderEmailInfo = (orderId: string) => {
  return useQuery<OrderEmailInfo[]>({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'email-info'],
    queryFn: () => adminOrdersApi.getOrderEmailInfo(orderId),
    enabled: !!orderId,
  });
};


export const useShareableLink = (orderId: string) => {
  return useQuery<{ url: string }>({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'shareable-link'],
    queryFn: () => adminOrdersApi.getShareableLink(orderId),
    enabled: !!orderId,
    staleTime: 30 * 60 * 1000,
  });
};

export const useOrderCommissionData = (orderId: string) => {
  return useQuery<OrderCommissionData | null>({
    queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'commission-data'],
    queryFn: () => adminOrdersApi.getOrderCommissionData(orderId),
    enabled: !!orderId,
  });
};

type CommissionFileKey = 'payFromCustomerFile' | 'payToSupplierFile' | 'shippingFile' | 'okSalesTaxFile' | 'customerRefundFile' | 'supplierRefundFile';

export const useUpdateOrderCommission = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    OrderCommissionData,
    Error,
    { data: UpdateOrderCommissionRequest; files?: Partial<Record<CommissionFileKey, File>> }
  >({
    mutationFn: ({ data, files }) => adminOrdersApi.updateOrderCommissionData(orderId, data, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS, 'admin', orderId, 'commission-data'] });
      toast.success('Commission data saved');
    },
    onError: () => {
      toast.error('Failed to save commission data');
    },
  });
};
